(ns nosco.richtext.toolbar
  (:require
   ["nosco-prosemirror/list_commands" :as list-commands]
   ["nosco-prosemirror/utils" :as nosco-prosemirror-utils]
   ["prosemirror-commands" :refer [setBlockType toggleMark]]
   ["prosemirror-schema-list" :refer [liftListItem sinkListItem wrapInList]]
   ["prosemirror-state" :as prosemirror-state]
   ["react" :as React]
   ["react-roving-tabindex" :refer [RovingTabIndexProvider useFocusEffect
                                    useRovingTabIndex]]
   [goog.object :as gobj]
   [hx.hooks :refer [useState]]
   [hx.react :as hx :refer [defnc]]
   [nosco.controllers :as controllers]
   [nosco.graphql-client]
   [nosco.i18n.translate :as t]
   [nosco.icons2 :as icons2]
   [nosco.react :as nosco-react]
   [nosco.richtext.merge-tags :as merge-tags]
   [nosco.richtext.uploads :as uploads]
   [nosco.richtext.urls :as urls]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.menus :as menus]
   [nosco.ui.pills :as pills]
   [nosco.ui.popup :as popup]
   [nosco.ui.tooltips :as tooltips]
   [nosco.util :as util]))

;; this is super annoying in dev mode, as it can't handle hot-code reloading
;; and it spams the console, so we disable it via a closure-define
(goog-define DISABLE-ROVING-TAB-INDEX false)

(defn use-tab-index []
  (when-not DISABLE-ROVING-TAB-INDEX
    (let [roving-tabindex-ref (React/useRef nil)
          [tab-index focused handle-key-down] (useRovingTabIndex roving-tabindex-ref)]
      (useFocusEffect focused roving-tabindex-ref)
      {:tab-index tab-index
       :handle-key-down handle-key-down
       :roving-tabindex-ref roving-tabindex-ref})))

(defnc ToolbarButton [{:keys [icon variant size label on-click active disabled tooltip-label loading]
                       :or {variant :icon-gray}} ref]
  (let [{:keys [tab-index handle-key-down roving-tabindex-ref]} (use-tab-index)
        [tooltip-props tooltip-trigger-ref] (tooltips/use-tooltip {:popper-opts {:placement "bottom"
                                                                                 :offset [0 2]}})]
    [:<>
     [tooltips/Tooltip
      (cond-> tooltip-props
        (nil? tooltip-label) (assoc :visible false)
        active (assoc :visible false)
        :always (assoc :label tooltip-label))]
     [buttons2/Button
      (cond->
       {:variant variant
        :label label
        :aria-label tooltip-label
        :disabled disabled
        :ref (nosco-react/merge-refs roving-tabindex-ref tooltip-trigger-ref ref)
        :on-click on-click
        :activated active
        :icon icon
        :tab-index tab-index
        :on-key-down handle-key-down
        :loading loading}
        size (assoc :size size))]]))

(nosco-react/forward-ref! ToolbarButton)

(defnc ToolbarSeparator []
  [:span {:class "nosco-toolbar-separator"}])

(defnc ToolbarPopup [{:keys [component variant icon label tooltip-label disabled children]
                      :or {component ToolbarButton}}]
  (let [[ref-cb popper] (popup/use-popper {:placement "bottom-start"
                                           :flip true})
        button-ref (React/useRef)
        popup-ref (React/useRef)
        [visible set-visible] (useState false)]
    ;; use this so that the popup actually closes whenever *anything*
    ;; is clicked (including the dropdown buttons)
    (popup/use-click-outside
     visible
     (fn []
       ;; use setTimeout because otherwise the actual on-click
       ;; of the menu item doesn't register, worth investigating
       (js/setTimeout #(set-visible false))) button-ref #_popup-ref)
    ;; we don't use dropdowns2 because it interferes with the focus,
    ;; whereas in the editor toolbar, we want to manage the focus
    ;; so that the editor regains focus after a click
    [:<>
     [component (cond-> {:icon icon
                         :ref (nosco-react/merge-refs button-ref ref-cb)
                         :disabled disabled
                         :active visible
                         :label label
                         :tooltip-label tooltip-label
                         :on-click #(set-visible not)}
                  variant (assoc :variant variant))]
     [popup/Overlay {:popper popper
                     :ref popup-ref
                     :open visible}
      [menus/Menu
       [menus/MenuSection
        children]]]]))

(defn- get-mark [schema mark-type]
  (let [marks (.-marks schema)
        mark (gobj/get marks mark-type)]
    mark))

(defn- get-node [schema node-type]
  (let [nodes (.-nodes schema)
        node (gobj/get nodes node-type)]
    node))

(defn mark-active? [^js state ^js type]
  (let [selection (gobj/get state "selection")
        from (gobj/get selection "from")
        $from (gobj/get selection "$from")
        $from-marks (.marks $from)
        stored-marks (.-storedMarks state)
        to (gobj/get selection "to")
        empty (gobj/get selection "empty")]
    (if empty
      (.isInSet type (or stored-marks $from-marks))
      (let [doc (.-doc state)
            has-mark (.rangeHasMark doc from to type)]
        has-mark))))

(defn- on-mark-toggle [mark ^js view]
  (fn [e]
    (.preventDefault e)
    ((toggleMark mark)
     (.-state view)
     (.-dispatch view))
    (.focus view)
    view))

(defn- block-type? [state node-type]
  (let [selection (gobj/get state "selection")
        $from (gobj/get selection "$from")
        to (gobj/get selection "to")
        ^js node (gobj/get selection "node")]
    (if node
      (.hasMarkup node node-type)
      (let [^js parent (gobj/get $from "parent")]
        (and (<= to (.end $from))
             (.hasMarkup parent node-type))))))

(defn- heading-level? [state heading-node level]
  (let [selection (gobj/get state "selection")
        $from (gobj/get selection "$from")
        to (gobj/get selection "to")
        ^js node (gobj/get selection "node")
        node (if node node
                 (let [^js parent (gobj/get $from "parent")]
                   (when (<= to (.end $from))
                     parent)))]
    (when node
      (.hasMarkup node heading-node #js {:level level}))))

(defn- on-set-heading-level [heading-type level ^js view]
  (fn [e]
    (.preventDefault e)
    (let [cmd (setBlockType heading-type #js {:level level})]
      (cmd
       (.-state view)
       (.-dispatch view))
      (.focus view)
      view)))

(defn- on-set-block-type [node-type ^js view]
  (fn [e]
    (.preventDefault e)
    (let [cmd (setBlockType node-type)]
      (cmd
       (.-state view)
       (.-dispatch view))
      (.focus view)
      view)))

(defn- on-set-alignment [align ^js view]
  ;; this assumes that the top-level block node supports an attribute
  ;; named "align". This is true now for paragraphs only but it should
  ;; be also for headings and figures and anything that needs alignment
  (fn [^js event]
    (.preventDefault event)
    (let [pos (.. view -state -selection -$from (before 1))
          tr (.. view -state -tr (setNodeMarkup pos nil #js {:data-align align}))]
      (.dispatch view tr)
      (when (instance? prosemirror-state/TextSelection (.. view -state -selection))
        (.focus view)))))

(defn- is-list [type-name]
  (get #{"bullet_list" "ordered_list"} type-name))

;; Taken from tiptap.
;; https://github.com/ueberdosis/tiptap/blob/develop/packages/core/src/commands/toggleList.ts
(defn- on-toggle-list [^js list-type item-type ^js view]
  (fn [e]
    (.preventDefault e)
    (when view
      (let [state (.-state view)
            dispatch (.-dispatch view)
            selection-from (.-selection.$from state)
            range (.blockRange selection-from)]
        (when range
          (let [^js parent-list (nosco-prosemirror-utils/findParentNodeClosestToPos selection-from (fn [^js node] (is-list (.-type.name node))))
                parent-list-check (and (>= (.-depth range) 1) parent-list (<= (- (.-depth range) (.-depth parent-list)) 1))]
            (cond
              ;; Remove list type
              (and parent-list-check
                   (= (.-node.type.name parent-list) list-type))
              (let [cmd (liftListItem item-type)]
                (cmd state)
                (.focus view))
              ;; Change list type
              (and parent-list-check
                   (is-list (.-node.type.name parent-list))
                   (.validContent list-type (.-node.content parent-list)))
              (let [tr (.-tr state)]
                (.setNodeMarkup tr (.-pos parent-list) list-type)
                (list-commands/joinListBackwards tr list-type)
                (list-commands/joinListForwards tr list-type)
                (.dispatch view tr)
                (.focus view))
              :else
              (let [wrap-in-list-cmd (wrapInList list-type)
                    can-wrap-in-list (wrap-in-list-cmd state)]
                (if can-wrap-in-list
                  (do (wrap-in-list-cmd state dispatch)
                      (.focus view))
                  false)))))))))

(defn- can-indent-list-item? [node-type ^js view]
  (when view
    (let [cmd (sinkListItem node-type)]
      (cmd (.-state view)))))

(defn- can-outdent-list-item? [node-type ^js view]
  (when view
    (let [cmd (liftListItem node-type)]
      (cmd (.-state view)))))

(defn- on-indent-list-item [node-type ^js view]
  (fn [e]
    (.preventDefault e)
    (let [cmd (sinkListItem node-type)]
      (cmd (.-state view)
           (.-dispatch view))
      (.focus view))))

(defn- on-outdent-list-item [node-type ^js view]
  (fn [e]
    (.preventDefault e)
    (let [cmd (liftListItem node-type)]
      (cmd (.-state view)
           (.-dispatch view))
      (.focus view))))

(defn- insert-merge-tag-node [^js view mt]
  (let [node
        (.. view -state -schema -nodes -merge_tag (create #js {:merge-tag mt}))
        transaction (-> (.. view -state -tr)
                        (.replaceSelectionWith node false))]
    (.dispatch view transaction)
    (.focus view)))

(defnc MergeTagsPill [{:keys [label on-click]} ref]
  [pills/PillButton
   {:ref ref
    :border true
    :size :condensed
    :label label
    :dropdown true
    :on-click on-click}])

(defn- insert-formula-ref-node [^js view formula-ref]
  (let [node
        (.. view -state -schema -nodes -formula_ref (create #js {:formula-ref (:id formula-ref)
                                                                 :title (str "#" (:title formula-ref))}))
        transaction (-> (.. view -state -tr)
                        (.replaceSelectionWith node false))]
    (.dispatch view transaction)
    (.focus view)))

(nosco-react/forward-ref! MergeTagsPill)

(defnc HTMLEditorToolbar
  [{:keys [schema
           state
           view
           layout
           formula-refs
           variant
           submit-render
           link-editor-modal-open
           on-link-editor-modal-open
           on-link-editor-modal-close
           ai-suggestions-open
           ai-toolbar-button-ref
           on-ai-click]
    :or {layout [:text-size
                 :text-style
                 :paragraph-style
                 :links
                 :images
                 :files
                 :videos
                 :merge-tags]
         variant :gray}}]
  {:pre [(#{:gray :white :bottom} variant)]}
  (let [strong (get-mark schema "strong")
        em (get-mark schema "em")
        link (get-mark schema "link")
        underline (get-mark schema "underline")
        bullet-list (get-node schema "bullet_list")
        ordered-list (get-node schema "ordered_list")
        list-item (get-node schema "list_item")
        heading (get-node schema "heading")
        paragraph (get-node schema "paragraph")

        [image-showing set-image-showing] (useState false)
        [video-showing set-video-showing] (useState false)
        [files-showing set-files-showing] (useState false)
        overlay-container-ref (React/useRef)
        {intersection-target-ref :ref intersection-target-in-view :in-view}
        (util/use-in-view {:initialInView true})
        button-variant (if (or (= variant :white)
                               (= variant :bottom))
                         :icon-light
                         :icon-gray)]
    ;; https://www.w3.org/TR/wai-aria-practices/#toolbar
    [:provider {:context popup/OverlayContainerContext
                :value (.-current overlay-container-ref)}
     [:div {:class "nosco-richtext-menubar__sticky-marker"
            :ref intersection-target-ref}]
     [:div {:class ["nosco-richtext-menubar"
                    (when-not intersection-target-in-view
                      "nosco-richtext-menubar--sticky")
                    (when (= :white variant)
                      "nosco-richtext-menubar--white")
                    (when (= :bottom variant)
                      "nosco-richtext-menubar--bottom")]
            :role "toolbar"}
      (let [layout-set (set layout)]
        [:<>
         (when (or (:images layout-set)
                   (:upload-combo layout-set))
           [uploads/ImageUploadPopup {:showing image-showing
                                      :on-close #(set-image-showing false)
                                      :view view}])
         (when (or (:files layout-set)
                   (:upload-combo layout-set))
           [uploads/FileUploadPopup {:showing files-showing
                                     :on-close #(set-files-showing false)
                                     :view view}])
         (when (or (:videos layout-set)
                   (:upload-combo layout-set))
           [uploads/VideoUploadPopup
            {:view view
             :showing video-showing
             :on-close #(set-video-showing false)}])
         (when (or (:links layout-set)
                   (:upload-combo layout-set))
           [urls/LinkEditorModal {:view view
                                  :link-mark-type link
                                  :showing link-editor-modal-open
                                  :on-close #(on-link-editor-modal-close)}])])
      [RovingTabIndexProvider
       [:<>
        (for [[layout-group index] (map vector layout (range))
              :let [separator? (< (inc index) (count layout))]]
          (case layout-group
            (:text-size :text-size--document)
            [:<>
             [ToolbarPopup {:icon icons2/Text-size-small
                            :tooltip-label (t/t "Text size")
                            :variant button-variant}
              [menus/MenuItemAdmin {:on-click (on-set-block-type paragraph view)
                                    :selected (block-type? state paragraph)
                                    :label (t/t "Normal text")}]
              (cond
                (= layout-group :text-size)
                [menus/MenuItemAdmin {:selected (block-type? state heading)
                                      :on-click (on-set-heading-level heading 3 view)
                                      :label (t/t "Heading")}]
                (= layout-group :text-size--document)
                [:<>
                 (for [level [1 2 3]]
                   [menus/MenuItemAdmin {:selected (heading-level? state heading level)
                                         :on-click (on-set-heading-level heading level view)
                                         :label (t/t "Heading %s" level)}])])]
             (when separator?
               [ToolbarSeparator])]

            :text-style
            [:<>
             [ToolbarButton {:icon icons2/Text-StyleBold-small
                             :tooltip-label (t/t "Bold")
                             :active (mark-active? state strong)
                             :variant button-variant
                             :on-click (on-mark-toggle strong view)}]
             [ToolbarButton {:icon icons2/Text-StyleItalic-small
                             :tooltip-label (t/t "Italic")
                             :active (mark-active? state em)
                             :variant button-variant
                             :on-click (on-mark-toggle em view)}]
             [ToolbarButton {:icon icons2/Text-StyleUnderline-small
                             :tooltip-label (t/t "Underline")
                             :active (mark-active? state underline)
                             :variant button-variant
                             :on-click (on-mark-toggle underline view)}]
             (when separator?
               [ToolbarSeparator])]

            :text-style-combo
            [ToolbarPopup {:icon icons2/Text-StyleBold-small
                           :tooltip-label (t/t "Text style")
                           :variant button-variant}
             [menus/MenuItemAdmin {:icon icons2/Text-StyleBold-small
                                   :label (t/t "Bold")
                                   :selected (mark-active? state strong)
                                   :on-click (on-mark-toggle strong view)}]
             [menus/MenuItemAdmin {:icon icons2/Text-StyleItalic-small
                                   :label (t/t "Italic")
                                   :selected (mark-active? state em)
                                   :on-click (on-mark-toggle em view)}]
             [menus/MenuItemAdmin {:icon icons2/Text-StyleUnderline-small
                                   :label (t/t "Underline")
                                   :selected (mark-active? state underline)
                                   :on-click (on-mark-toggle underline view)}]]

            :paragraph-style
            [:<>
             [ToolbarPopup {:icon icons2/AlignLeft-small
                            :tooltip-label (t/t "Alignment")
                            :variant button-variant}
              [menus/MenuItemAdmin {:icon icons2/AlignLeft-small
                                    :on-click (on-set-alignment "left" view)
                                    :label (t/t "Left")}]
              [menus/MenuItemAdmin {:icon icons2/AlignRight-small
                                    :on-click (on-set-alignment "right" view)
                                    :label (t/t "Right")}]
              [menus/MenuItemAdmin {:icon icons2/AlignCenter-small
                                    :on-click (on-set-alignment "center" view)
                                    :label (t/t "Center")}]]
             [ToolbarPopup {:icon icons2/ListBullets-small
                            :tooltip-label (t/t "Indentation")
                            :variant button-variant}
              [menus/MenuItemAdmin {:icon icons2/ListBullets-small
                                    :on-click (on-toggle-list bullet-list list-item view)
                                    :label (t/t "Bulleted List")}]
              [menus/MenuItemAdmin {:icon icons2/ListNumbered-small
                                    :on-click (on-toggle-list ordered-list list-item view)
                                    :label (t/t "Ordered List")}]
              [menus/MenuItemAdmin {:icon "icon-margin-left"
                                    :label (t/t "Indent")
                                    :on-click (on-indent-list-item list-item view)
                                    :disabled (not (can-indent-list-item? list-item view))}]
              [menus/MenuItemAdmin {:icon "icon-margin-right"
                                    :label (t/t "Outdent")
                                    :on-click (on-outdent-list-item list-item view)
                                    :disabled (not (can-outdent-list-item? list-item view))}]]
             (when separator?
               [ToolbarSeparator])]

            :links
            [:<>
             [ToolbarButton {:icon icons2/Link-small
                             :on-click #(if link-editor-modal-open
                                          (on-link-editor-modal-close)
                                          (on-link-editor-modal-open))
                             :tooltip-label (t/t "Insert link")
                             :variant button-variant}]
             (when separator?
               [ToolbarSeparator])]

            :upload-combo
            [ToolbarPopup {:icon icons2/Add-small
                           :variant button-variant}
             [menus/MenuItemAdmin {:icon icons2/Link-small
                                   :label (t/t "Insert link")
                                   :on-click #(if link-editor-modal-open
                                                (on-link-editor-modal-close)
                                                (on-link-editor-modal-open))}]
             [menus/MenuItemAdmin {:icon icons2/Image-small
                                   :on-click #(set-image-showing true)
                                   :label (t/t "Insert image")}]
             [menus/MenuItemAdmin {:icon icons2/Video-small
                                   :on-click #(set-video-showing true)
                                   :label (t/t "Insert video")}]
             [menus/MenuItemAdmin {:icon icons2/Attach-small
                                   :on-click #(set-files-showing true)
                                   :label (t/t "Insert file")}]]

            :images
            [ToolbarButton {:icon icons2/Image-small
                            :on-click #(set-image-showing true)
                            :tooltip-label (t/t "Insert image")
                            :variant button-variant}]
            :files
            [ToolbarButton {:icon icons2/Attach-small
                            :on-click #(set-files-showing true)
                            :tooltip-label (t/t "Insert file")
                            :variant button-variant}]

            :videos
            [ToolbarButton {:icon icons2/Video-small
                            :on-click #(set-video-showing true)
                            :tooltip-label (t/t "Insert video")
                            :variant button-variant}]

            :ai
            (when (:aiEnabled (controllers/get-conf!))
              [:<>
               [ToolbarSeparator]
               [:span {:class "nosco-richtext-ai-button"}
                [ToolbarButton {:ref ai-toolbar-button-ref
                                :icon icons2/AI-small
                                :tooltip-label (t/t "Nosco AI")
                                :variant button-variant
                                :on-click on-ai-click
                                :active ai-suggestions-open}]]])

            :merge-tags
            [:<>
             [ToolbarSeparator]
             [ToolbarPopup {:component MergeTagsPill
                            :label (t/t "Merge tags")}
              [menus/MenuItemAdminHeader {:text (t/t "Member data")}]
              (for [mt ["fname" "lname" "email"]
                    :let [mt (str "%recipient." mt "%")]]
                [menus/MenuItemAdmin {:label (merge-tags/merge-tag->text mt)
                                      :on-click #(insert-merge-tag-node view mt)}])
              [menus/MenuItemAdminHeader {:text (t/t "Links")}]
              (for [mt ["resetLink" "activationLink"]
                    :let [mt (str "%recipient." mt "%")]]
                [menus/MenuItemAdmin {:label (merge-tags/merge-tag->text mt)
                                      :on-click #(insert-merge-tag-node view mt)}])]]
            :formula-refs
            [:<>
             [ToolbarPopup {:icon icons2/Number-small
                            :tooltip-label (t/t "Numeric field")
                            :variant button-variant}
              (for [formula-ref formula-refs]
                [menus/MenuItemAdmin {:label (:title formula-ref)
                                      :on-click #(insert-formula-ref-node view formula-ref)}])]]))
        (when submit-render
          [:div {:class "nosco-toolbar-submit"}
           (submit-render)])]]]
     ;; Put here and with a z-index such the the tooltips are display on top of the surrounding nodes
     ;; The ref is not put on the toolbar itself because the toolbar uses a CSS "gap". The gap would be also added
     ;; on the tooltip containers
     [:div {:ref overlay-container-ref
            :style {:z-index "2"}}]]))
